import { useGlobalNotification } from 'hooks';
import { useDownloadFile, useFileDeleteMutation, useFilesListQuery } from 'queries';
import { useCommentMutation, useInvalidateRecordsQueries } from 'queries/events/records';
import { KeyTypesUpload, RecordCategory } from 'queries/events/records/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadBase64File } from 'utils';

interface SaveEvidencesProps {
  eventId: string;
}

export const useSaveEvidences = ({ eventId }: SaveEvidencesProps) => {
  const { t } = useTranslation();
  const { displaySnackbar } = useGlobalNotification();
  const refetchAlarmQueries = useInvalidateRecordsQueries(eventId);
  const newCommentMutation = useCommentMutation();
  const [fileToDownload, setFileToDownload] = useState<string | null>(null);
  const fileKeys = { keyName: eventId, keyType: KeyTypesUpload.RECORDS };
  const {
    data: filesData,
    refetch: refetchListFiles,
    isFetching,
    isLoading,
  } = useFilesListQuery({
    ...fileKeys,
    showAuditLog: true,
  });
  const { data: dataToDownload, refetch: refetchDownload } = useDownloadFile({
    ...fileKeys,
    fileName: fileToDownload || '',
  });
  const deleteMutation = useFileDeleteMutation();

  const attachments = filesData?.files.length || 0;
  const isLoadingFiles = isFetching || isLoading;

  useEffect(() => {
    if (fileToDownload && dataToDownload) {
      refetchDownload();
      downloadBase64File(dataToDownload, fileToDownload);

      setFileToDownload(null);
    }
  }, [fileToDownload, dataToDownload]);

  const handleSubmitComment = (payload: string) => {
    newCommentMutation.mutate(
      {
        eventId,
        text: payload,
        category: RecordCategory.COMMENT,
      },
      {
        onSuccess: () => {
          refetchListFiles();
        },
        onError: () => {
          displaySnackbar({
            message: t('messages:fetch_error.message'),
            type: 'error',
          });
          refetchListFiles();
        },
        onSettled: () => {
          refetchAlarmQueries();
        },
      },
    );
  };

  return {
    attachments,
    handleSubmitComment,
    isLoading: isLoadingFiles,
    filesData,
    refetchListFiles,
    deleteMutation,
    fileToDownload,
    setFileToDownload,
    fileKeys,
    refetchAlarmQueries,
  };
};
